import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Questionnaire.css';

function Questionnaire({ filingId, onSubmit, loading, priorYearData }) {
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isQuestionVisible = (q, allResponses) => {
    if (!q?.dependsOn || q.dependsOn.length === 0) return true;
    return q.dependsOn.every(dep => {
      const actual = allResponses?.[dep.questionId];
      return actual === dep.answer;
    });
  };

  const visibleQuestions = questions.filter(q => isQuestionVisible(q, responses));

  useEffect(() => {
    if (currentQuestion > 0 && currentQuestion >= visibleQuestions.length) {
      setCurrentQuestion(Math.max(visibleQuestions.length - 1, 0));
    }
  }, [currentQuestion, visibleQuestions.length]);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setQuestionsLoading(true);
      const response = await axios.get('/api/questions');
      setQuestions(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load questions');
      console.error(err);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setResponses({
      ...responses,
      [questionId]: answer
    });
  };

  const handleNext = () => {
    if (currentQuestion < visibleQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    const responseArray = visibleQuestions.map(q => ({
      questionId: q.id,
      answer: responses[q.id] || ''
    }));
    onSubmit(responseArray);
  };

  const clampedIndex = Math.min(currentQuestion, Math.max(visibleQuestions.length - 1, 0));
  const isAnswered = responses[visibleQuestions[clampedIndex]?.id] !== undefined;
  const progress = visibleQuestions.length > 0 ? Math.round(((clampedIndex + 1) / visibleQuestions.length) * 100) : 0;

  if (questionsLoading) {
    return <div className="loading">Loading questions...</div>;
  }

  if (questions.length === 0) {
    return <div className="error">No questions available</div>;
  }

  if (visibleQuestions.length === 0) {
    return <div className="error">No questions available</div>;
  }

  const question = visibleQuestions[clampedIndex];
  const allAnswered = visibleQuestions.every(q => responses[q.id] !== undefined);

  return (
    <div className="questionnaire-container">
      <h2>Step 2: Answer Questions About Your Tax Situation</h2>
      
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>
      <p className="progress-text">
        Question {clampedIndex + 1} of {visibleQuestions.length}
      </p>

      <div className="question-card">
        <h3>{question.text}</h3>

        <div className="question-input">
          {question.type === 'yes-no' && (
            <div className="options">
              <button
                className={`option-button ${responses[question.id] === 'Yes' ? 'selected' : ''}`}
                onClick={() => handleAnswerChange(question.id, 'Yes')}
              >
                Yes
              </button>
              <button
                className={`option-button ${responses[question.id] === 'No' ? 'selected' : ''}`}
                onClick={() => handleAnswerChange(question.id, 'No')}
              >
                No
              </button>
            </div>
          )}

          {question.type === 'select' && (
            <div className="select-container">
              <select
                value={responses[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                className="select-input"
              >
                <option value="">Select an option...</option>
                {question.options?.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}

          {question.type === 'text' && (
            <input
              type="text"
              value={responses[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className="text-input"
              placeholder="Enter your answer"
            />
          )}
        </div>
      </div>

      <div className="navigation-buttons">
        <button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className="nav-button"
        >
          ← Previous
        </button>

        {clampedIndex < visibleQuestions.length - 1 ? (
          <button
            onClick={handleNext}
            disabled={!isAnswered || loading}
            className="nav-button primary"
          >
            Next →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!allAnswered || loading}
            className="nav-button primary submit"
          >
            {loading ? 'Processing...' : 'Get Recommendations'}
          </button>
        )}
      </div>
    </div>
  );
}

export default Questionnaire;
